import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { IpAssetDialog } from "@/components/app/IpAssetDialog";
import { IpNotice } from "./IpAssets";
import { api, errorMessage } from "@/lib/api";
import { IP_TYPES, reminderStatus, formatDate } from "@/lib/format";

const KIND = { window_opens: "Filing window opens", due_date: "Due date", grace_ends: "Grace period ends (extra fee)", other: "Other date" };
const DAYS = [180, 90, 30, 7];

const Schedule = ({ s, onConfirm, confirmedKeys, onToggleDone }) => (
  <Panel title="Suggested dates" testId="ip-schedule-panel">
    <div className="space-y-3 p-5 text-sm">
      <p data-testid="ip-schedule-message" className={s.supported ? "text-slate-700" : "rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900"}>{s.message}</p>
      {s.events.map((e) => (
        <div key={e.title} className={`rounded-md border p-3 ${e.completed ? "opacity-60" : ""}`} data-testid="ip-schedule-event">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-medium text-navy">{e.title}</p>
            <span data-testid={`ip-event-label-${e.key}`} className={`rounded-full px-2 py-0.5 text-[11px] ${e.completed ? "bg-slate-100 text-slate-600" : "bg-amber-50 text-amber-900"}`}>{e.label}</span>
          </div>
          <label className="mt-1 flex items-center gap-2 text-xs text-slate-600"><Checkbox checked={e.completed} onCheckedChange={() => onToggleDone(e.key)} data-testid={`ip-event-done-${e.key}`} /> My records show this is already done</label>
          {!e.completed && ["window_opens", "due_date", "grace_ends"].map((k) => {
            const key = `${e.title}|${k}`;
            return (
              <div key={k} className="mt-1 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">{KIND[k]}: <span className="font-mono">{formatDate(e[k])}</span></span>
                  {confirmedKeys.has(key) ? <span className="text-emerald-700">Confirmed by you</span>
                    : <button onClick={() => onConfirm(e, k)} data-testid={`ip-confirm-${k}`} className="font-medium text-navy underline">I've checked this. Confirm.</button>}
                </div>
                {e[`${k}_next_business_day`] && <p data-testid={`ip-next-business-${k}`} className="text-[11px] text-slate-500">Falls on a weekend or federal holiday. The USPTO accepts the next business day: {formatDate(e[`${k}_next_business_day`])}</p>}
              </div>);
          })}
        </div>))}
      {s.notes.map((n) => <p key={n} className="text-xs text-slate-500">• {n}</p>)}
      {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-navy underline" data-testid="ip-rules-source">USPTO source (rules checked {s.rules_verified_on}) <ExternalLink className="h-3 w-3" /></a>}
      <p className="text-xs text-slate-500" data-testid="ip-schedule-disclaimer">{s.disclaimer}</p>
    </div>
  </Panel>
);

const ManualDate = ({ onAdd }) => {
  const [f, setF] = useState({ label: "", date: "", confirmed: true });
  return (
    <div className="grid gap-2 border-t p-5 sm:grid-cols-[1fr_auto_auto_auto] sm:items-center" data-testid="ip-manual-date-form">
      <Input placeholder="What is due? (from an official source)" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="ip-manual-label-input" className="h-10" />
      <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="ip-manual-date-input" className="h-10" />
      <label className="flex items-center gap-2 text-xs"><Checkbox checked={f.confirmed} onCheckedChange={(v) => setF({ ...f, confirmed: !!v })} data-testid="ip-manual-confirmed" /> Confirmed</label>
      <Button size="sm" disabled={!f.label || !f.date} onClick={() => { onAdd({ kind: "due_date", ...f, source_note: "Entered manually by customer" }); setF({ label: "", date: "", confirmed: true }); }} data-testid="ip-manual-add-button" className="bg-navy hover:bg-navy-2">Add date</Button>
    </div>
  );
};

export default function IpAssetDetail() {
  const { workspace } = useOutletContext();
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const base = `/workspaces/${workspace.id}`;
  const load = useCallback(() => { api.get(`${base}/ip-assets/${assetId}`).then(({ data }) => setData(data)).catch((e) => toast.error(errorMessage(e))); }, [base, assetId]);
  useEffect(load, [load]);
  const run = (p) => p.then(load).catch((e) => toast.error(errorMessage(e)));
  if (!data) return null;
  const { asset, dates, reminders, schedule } = data;
  const confirmedKeys = new Set(dates.map((d) => d.source_note?.startsWith("Suggested:") && d.source_note.slice(10)));

  const confirmSuggested = (e, kind) => run(api.post(`${base}/ip-assets/${assetId}/dates`, { kind, label: `${e.title}: ${KIND[kind].toLowerCase()}`, date: e[kind], confirmed: true, source_note: `Suggested:${e.title}|${kind}` }));
  const toggleDay = (d, n) => run(api.patch(`${base}/ip-dates/${d.id}`, { reminder_days: d.reminder_days.includes(n) ? d.reminder_days.filter((x) => x !== n) : [...d.reminder_days, n] }));
  const removeAsset = () => api.delete(`${base}/ip-assets/${assetId}`).then(() => navigate(`/app/w/${workspace.id}/ip`)).catch((e) => toast.error(errorMessage(e)));
  const toggleDone = (key) => {
    const { id, workspace_id, created_by, created_at, updated_at, ...body } = asset;
    const done = asset.completed || [];
    run(api.patch(`${base}/ip-assets/${assetId}`, { ...body, completed: done.includes(key) ? done.filter((k) => k !== key) : [...done, key] }));
  };

  return (
    <div data-testid="ip-detail-page">
      <PageHeader eyebrow={IP_TYPES[asset.asset_type]} title={asset.title} subtitle={`Owner: ${asset.owner}${asset.number ? ` · No. ${asset.number}` : ""}`}
        action={<div className="flex gap-2"><Button variant="outline" onClick={() => setEditing(true)} data-testid="ip-edit-button">Edit</Button><Button variant="outline" onClick={removeAsset} data-testid="ip-delete-button" className="text-red-700"><Trash2 className="h-4 w-4" /></Button></div>} />
      <div className="mb-6"><IpNotice /></div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Schedule s={schedule} onConfirm={confirmSuggested} confirmedKeys={confirmedKeys} onToggleDone={toggleDone} />
        <Panel title="Confirmed and saved dates" testId="ip-dates-panel">
          {!dates.length ? <Empty testId="ip-dates-empty">No dates saved yet.</Empty> : (
            <ul className="divide-y">{dates.map((d) => (
              <li key={d.id} data-testid={`ip-date-row-${d.id}`} className="space-y-2 px-5 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div><p className="text-sm font-medium text-navy">{d.label}</p><p className="font-mono text-xs text-slate-500">{formatDate(d.date)} · {d.confirmed ? "Confirmed by you" : "Not confirmed"}</p></div>
                  <button onClick={() => run(api.delete(`${base}/ip-dates/${d.id}`))} data-testid="ip-date-delete-button" className="text-slate-400 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                </div>
                {d.kind === "due_date" && d.confirmed && (
                  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">Save reminders:
                    {DAYS.map((n) => <label key={n} className="flex items-center gap-1"><Checkbox checked={d.reminder_days.includes(n)} disabled={workspace.is_sample} onCheckedChange={() => toggleDay(d, n)} data-testid={`ip-reminder-${n}-${d.id}`} />{n} days</label>)}
                  </div>)}
              </li>))}</ul>
          )}
          <ManualDate onAdd={(body) => run(api.post(`${base}/ip-assets/${assetId}/dates`, body))} />
          <p className="px-5 pb-4 text-xs text-amber-900" data-testid="ip-reminders-not-connected">{workspace.is_sample ? "Reminders are turned off in the sample workspace." : "Reminder emails go to workspace members who have upcoming reminders turned on in Settings, at about 8am on the reminder date."}</p>
        </Panel>
      </div>
      <Panel title="Reminder log" className="mt-6" testId="ip-reminders-list">
        {!reminders.length ? <Empty testId="ip-reminders-empty">No reminders saved.</Empty> : (
          <ul className="divide-y">{reminders.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 px-5 py-2 text-sm" data-testid={`ip-reminder-row-${r.id}`}>
              <span>{r.deadline_title} · {r.days_before} days before · {formatDate(r.remind_on)}</span>
              <span data-testid="ip-reminder-status" className="text-xs text-slate-600">{reminderStatus(r)}</span>
            </li>))}</ul>
        )}
      </Panel>
      {editing && <IpAssetDialog workspaceId={workspace.id} asset={asset} open onOpenChange={setEditing} onSaved={load} />}
    </div>
  );
}
